'use client';

import { useEffect, useSyncExternalStore } from 'react';
import { toast, Toaster, type ToasterProps } from 'sonner';

import { useThemeMinimal } from '../lib/theme/theme-provider-minimal';

const subscribe = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

/**
 * `sonner`'s `<Toaster>` bound to the app theme.
 *
 * Sonner defaults to `theme="light"` and never looks at the `.dark` class on
 * `<html>`, so a bare `<Toaster>` painted a white "Done" toast over the dark
 * app (Focus checkmark in the sidebar). Reading `resolvedTheme` here keeps the
 * toast, its rich colors and the Undo button in step with the theme toggle.
 * Must mount inside `ThemeProviderMinimal` (both apps' `MinimalProviders`).
 */
export const ThemedToaster = (props: Omit<ToasterProps, 'theme'>) => {
	const { resolvedTheme } = useThemeMinimal();
	// Notifications have no server content. Keep SSR and the first hydration
	// render empty, then mount outside the route's streamed Suspense content.
	const hydrated = useSyncExternalStore(subscribe, clientSnapshot, serverSnapshot);
	useEffect(() => {
		if (!hydrated) return;
		// Sonner subscribes in its child effect and doesn't replay its store.
		// Deliver notifications queued by page mount effects before it mounted.
		// Preserve IDs and options; Sonner updates existing IDs without duplicates.
		for (const queued of toast.getToasts()) {
			if ('dismiss' in queued) continue;
			if (props.id ? queued.toasterId === props.id : !queued.toasterId) {
				toast(queued.title, queued);
			}
		}
	}, [hydrated, props.id]);
	if (!hydrated) return null;
	return <Toaster theme={resolvedTheme} {...props} />;
};
